<template>
  <div class="pp-page">
    <AccessTopNav
      v-if="accessLevel === 'guest'"
      :level="accessLevel"
      @sign-in="onSignin"
      @sign-up="onSignup"
    />

    <div class="pp-wrap">
      <div
        v-if="!property"
        class="pp-state empty"
      >
        <h2>{{ t('property.shell.notAvailable.title') }}</h2>
        <p>{{ t('property.shell.notAvailable.description') }}</p>
        <NuxtLink
          :to="'/explore'"
          class="cta"
          >{{ t('property.shell.notAvailable.browseProjects') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></NuxtLink
        >
      </div>

      <template v-else>
        <GuestSignupBanner
          v-if="accessLevel === 'guest'"
          @sign-in="onSignin"
          @sign-up="onSignup"
        />
        <RegisteredVerifyChip
          v-else-if="accessLevel === 'registered'"
          @verify-click="onVerifyClick"
        />

        <div
          v-if="accessLevel !== 'guest'"
          class="pp-back-row"
        >
          <PropertyV2Back @click="goBack" />
          <PropertyV2Crumb
            :title="projectTitleDisplay"
            :developer-label="developerNameDisplay"
          />
        </div>
        <div
          v-else
          class="pa-crumbs-public"
        >
          <NuxtLink :to="'/explore'">{{ t('property.shell.breadcrumb.dubaiProjects') }}</NuxtLink>
          <span>›</span>
          <NuxtLink :to="'/explore'">{{ exploreArea }}</NuxtLink>
          <span>›</span>
          <span class="cur">{{ projectTitleDisplay }}</span>
        </div>

        <div class="pp-title-share-wrap">
          <PropertyV2TitleRow
            :title="projectTitleDisplay"
            :developer-name="developerNameDisplay"
            :developer-slug="developerSlug"
            :ref-label="refLabel"
            :meta-items="titleMetaItems"
            :is-favorite="isFavorite"
            @toggle-favorite="onToggleFavorite"
            @share="onShareClick"
          />
          <SharePopover
            :open="shareOpen"
            :level="accessLevel"
            :property="sharePropertyData"
            :agent-reference="agentReference"
            :brochure-url="verifiedBrochureUrl"
            :share-stats="shareStatsData ?? null"
            @close="shareOpen = false"
            @share="onShareTrack"
          />
        </div>

        <div class="pp-gallery-frame">
          <PropertyGallery :item="property" />
        </div>

        <div class="pp-main">
          <div class="pp-content">
            <!-- Jump into the AI marketing generator with THIS project preselected
                 (verified agents only — the generator is verified-phone gated). -->
            <NuxtLink
              v-if="accessLevel === 'verified' && propertyComplexIdRaw"
              class="pp-mkt-banner"
              :to="marketingBannerTo"
            >
              <span class="pp-mkt-spark">✦</span>
              <span class="pp-mkt-copy">
                <strong>{{ t('property.shell.marketingBanner.title') }}</strong>
                <span>{{ t('property.shell.marketingBanner.description') }}</span>
              </span>
              <span class="pp-mkt-cta"
                >{{ t('property.shell.marketingBanner.cta') }}
                <span
                  class="inline-block rtl:-scale-x-100"
                  aria-hidden="true"
                  >→</span
                ></span
              >
            </NuxtLink>

            <PropertyV2DeveloperBriefing
              v-if="briefingVideo && accessLevel === 'verified'"
              :title="briefingVideo.title"
              :description="briefingVideo.description"
              :developer-name="developerNameDisplay"
              :thumbnail="briefingVideo.thumbnail"
              :duration-label="briefingVideo.durationLabel"
              :meta-items="briefingVideo.metaItems"
              :cta-link="briefingVideo.link"
            />

            <div
              v-if="developerUpdate"
              class="pp-dev-update"
            >
              <span class="pp-dev-update__pill">{{
                isDeveloperSubmitted
                  ? t('devRepProjects.public.newFromDeveloper')
                  : t('devRepProjects.public.developerConfirmed')
              }}</span>
              <span v-if="developerUpdate.updatedAt">{{
                t('devRepProjects.public.updatedOn', { date: formatDeveloperUpdateDate(developerUpdate.updatedAt) })
              }}</span>
              <span v-if="developerUpdate.availability">{{ developerUpdate.availability }}</span>
              <span v-if="developerUpdate.propertyTypes.length">{{ developerUpdate.propertyTypes.join(' · ') }}</span>
              <span v-if="developerUpdate.commissionPromotion">{{
                t('devRepProjects.public.commissionPromotion', { text: developerUpdate.commissionPromotion })
              }}</span>
            </div>

            <UpcomingPresentations
              v-if="presentationsDeveloperId"
              :developer-id="presentationsDeveloperId"
              :project-id="presentationsProjectId"
            />

            <!-- 100% Commission: the agent's own holds on this project (release / view deal). -->
            <ExclusiveMyHolds
              v-if="showExclusive && exclusiveHolds.length"
              class="pp-exclusive-holds"
              :holds="exclusiveHolds"
              :clock-offset-ms="exclusiveUnitsLive.clockOffsetMs.value"
            >
              <template #hold-actions="{ hold }">
                <ExclusiveHoldActions
                  :hold="hold"
                  @released="refreshExclusiveUnits"
                  @view-deal="onViewExclusiveDeal"
                />
              </template>
            </ExclusiveMyHolds>

            <PropertyV2TabsRow
              v-if="accessLevel === 'verified'"
              :tabs="tabDefinitions"
              :active-id="activeTab"
              @select="setActiveTab"
            />
            <AccessTabsRow
              v-else
              :active="accessActiveTab"
              :hidden-tabs="hiddenTabsForLevel"
              :locked-tabs="lockedTabsForLevel"
              @tab-click="onAccessTabClick"
              @locked-click="onLockedTab"
            />

            <ExclusiveInsidersBanner v-if="showExclusive && !isClientMode && activeTab === 'overview'" />

            <!-- 100% Commission projects use the same tabs; their live exclusive units fill the Availability tab. -->
            <component
              :is="getTabComponent(activeTab)"
              :key="activeTab"
              :item="tabItem"
              :project-name="projectTitle"
              @switch-tab="setActiveTab"
              @book="onBookUnit"
              @unit-select="onSelectAvailabilityRow"
            >
              <template
                v-if="exclusiveUnitsPayload"
                #unit-action="{ row }"
              >
                <ExclusiveUnitActionCell
                  v-if="isHoldableExclusiveRow(row)"
                  :can-hold="exclusiveUnitsPayload.canHold"
                  :hold-lock-reason="exclusiveUnitsPayload.holdLockReason"
                  :uae-next-step="exclusiveUnitsPayload.uaeNextStep ?? null"
                  :disabled="exclusiveUnitsLive.isFailing.value"
                  @hold="onHoldExclusiveRow(row)"
                  @verify-phone="onVerifyClick"
                />
                <span
                  v-else-if="row.catalogueUnitId !== null"
                  aria-hidden="true"
                  >—</span
                >
                <button
                  v-else
                  type="button"
                  class="pp-book-fallback"
                  @click="onBookUnit"
                >
                  {{ t('property.v2Availability.book') }}
                </button>
              </template>
            </component>

            <ExclusiveUnitDialog
              v-if="selectedExclusiveUnit"
              :unit="selectedExclusiveUnit"
              :booking-contact-name="exclusiveUnitsPayload?.contacts?.booking.name ?? null"
              :can-hold="!!exclusiveUnitsPayload?.canHold && !exclusiveUnitsLive.isFailing.value"
              :hide-offer="isClientMode"
              @close="selectedExclusiveUnit = null"
              @prepare-offer="onPrepareExclusiveOffer"
              @hold="onHoldFromUnitDialog"
            />

            <ExclusiveHoldDialog
              v-if="holdingExclusiveUnit && exclusiveCatalogueProjectId"
              :unit="holdingExclusiveUnit"
              :catalogue-project-id="exclusiveCatalogueProjectId"
              :project-name="projectTitleDisplay"
              :hold-hours="exclusiveUnitsPayload?.holdHours ?? null"
              :hide-earnings="isClientMode"
              @held="refreshExclusiveUnits"
              @close="holdingExclusiveUnit = null"
            />

            <p
              v-if="exclusiveEnded"
              class="pp-exclusive-ended"
              role="status"
            >
              {{ t('exclusiveUnits.section.noLongerExclusive') }}
            </p>

            <ProjectNewsFeedTeaser
              v-if="accessLevel === 'guest'"
              :project-title="projectTitleDisplay"
              :items="newsTeaserItems"
              :hidden-count="newsHiddenCount"
              @cta-click="onSignup"
            />

            <PropertyV2DeveloperVaultSection
              v-if="propertyPostgresId && accessLevel === 'verified'"
              :project-id="propertyPostgresId"
              :developer-name="developerNameDisplay"
            />
          </div>

          <aside class="pp-side">
            <PublicPriceCard
              v-if="accessLevel === 'guest' && publicPriceProperty"
              :property="publicPriceProperty"
              :unit-bands="publicUnitBands"
              @enquire-click="onEnquireClick"
              @save-click="onSaveClick"
            />
            <PropertyV2HeroCard
              v-else
              :title="projectTitleDisplay"
              :price-min="propertyDetailPriceMin"
              :currency="preferences.currency"
              :unit-types="availableUnitTypes"
              :developer-name="developerNameDisplay"
              :address="property.address"
              :progress-percentage="progressPercentage"
              :completion-label="completionLabel"
              :brochure-url="brochureUrl"
              :offer-link="offerLink"
              :sales-status-label="salesStatusLabel"
              :status-color="salesStatusColor"
              :is-generating-offer="isGeneratingOffer"
              :has-sellable-units="propertyUnits.length > 0 && !coBrokeNotice"
              @generate-offer="onGenerateOfferClick"
            />

            <LockedCard
              v-if="accessLevel === 'guest'"
              variant="green"
              :title="t('property.shell.commissionLockedCard.title')"
              :message="t('property.shell.commissionLockedCard.message')"
              :cta="t('property.shell.signUpFreeArrow')"
              cta-arrow
              :cta2="t('property.shell.signIn')"
              @cta-click="onSignup"
              @cta2-click="onSignin"
            />
            <CommissionEstimateCard
              v-else-if="accessLevel === 'registered'"
              :developer-name="developerNameDisplay || t('property.shell.theDeveloper')"
              :tier-key="'BRONZE'"
              :tier-percent="60"
              :sample-unit-label="sampleUnitLabel"
              :sample-brokerage-aed="sampleBrokerageAed"
              :sample-share-aed="sampleShareAed"
              @verify-click="onVerifyClick"
            />
            <p
              v-else-if="!isClientMode && coBrokeNotice"
              class="rounded-2xl border border-border-subtle bg-white p-4 text-sm leading-relaxed text-black-80"
              role="status"
            >
              {{ coBrokeNotice }}
            </p>
            <ExclusiveCommissionBlock
              v-else-if="showExclusiveCommission && exclusiveDetail"
              :detail="exclusiveDetail"
              :selected-earnings-aed="null"
            />
            <PropertyV2CommissionBlock
              v-else-if="!isClientMode && !showExclusive"
              v-model="selectedUnitKey"
              :is-auth="isAuth"
              :is-agent="!!isAgentForCommission"
              :units="propertyUnits"
              :tier-label="agentTierLabel"
              :pool-percent="projectCommissionPoolPercent"
              :tier-split-percent="agentTierSplitPercent"
              :commission-percent="commissionDisplay"
              :estimated-amount="estimatedCommissionValue"
              :currency="commissionEstimateCurrency"
              :tooltip="agentCommissionTooltip"
              :is-uae-share="isAgentUaeShare"
              :split-pending="isAgentSplitPending"
              @register="registerSelectedUnit"
            />

            <ExclusiveAboutProject
              v-if="showExclusive && exclusiveDetail?.whyExclusive"
              :text="exclusiveDetail.whyExclusive"
            />

            <ExclusiveProjectContacts
              v-if="showExclusive && !isClientMode"
              :contacts="exclusiveUnitsPayload?.contacts ?? null"
            />

            <LockedCard
              v-if="accessLevel === 'guest' && !showExclusive"
              variant="cream"
              :title="t('property.shell.developerContactsLockedCard.title')"
              :message="
                t('property.shell.developerContactsLockedCard.message', {
                  developerName: developerNameDisplay || t('property.shell.theDeveloper'),
                })
              "
              :cta="t('property.shell.signUpFreeArrow')"
              cta-arrow
              @cta-click="onSignup"
            />
            <DevAccountManagerLocked
              v-else-if="accessLevel === 'registered' && developerManagers.length && !showExclusive"
              :developer-name="developerNameDisplay || t('property.shell.theDeveloper')"
              :managers="developerManagers"
              @verify-click="onVerifyClick"
            />

            <!-- Exclusive projects are sold through Insiders: no developer sales contacts. -->
            <DevSalesManagerCard
              v-if="accessLevel === 'verified' && developerManagersVerified.length && !showExclusive"
              :developer-name="developerNameDisplay || t('property.shell.theDeveloper')"
              :managers="developerManagersVerified"
            />

            <PropertyV2AccountManagerSidebar
              v-if="accessLevel === 'verified'"
              :manager="accountManager"
            />

            <GuestExploreNearby
              v-if="accessLevel === 'guest' && exploreNearby.length"
              :area="exploreArea"
              :nearby="exploreNearby"
              @item-click="onExploreItemClick"
            />
          </aside>
        </div>

        <GuestBottomCTA
          v-if="accessLevel === 'guest'"
          @sign-up="onSignup"
          @how-it-works="onSignup"
        />
        <RegisteredBottomVerifyBanner
          v-else-if="accessLevel === 'registered'"
          @verify-click="onVerifyClick"
        />
      </template>
    </div>

    <AccessGateModalsHost />
    <SalesOfferWizard
      v-if="property && propertyComplexIdRaw"
      :open="offerDialogOpen"
      :item="tabItem"
      :property-id="String(propertyComplexIdRaw)"
      :initial-unit-id="selectedUnitKey"
      @close="offerDialogOpen = false"
    />
    <!-- Exclusive (catalogue) units from the unit popup keep their own one-step offer (no price hold). -->
    <SalesOfferDialog
      :open="!!exclusiveOfferUnit"
      :pending="exclusiveSalesOffer.isPending.value"
      @close="closeOfferDialog"
      @submit="submitExclusiveFromDialog"
    />
  </div>
</template>

<script setup lang="ts">
import {
  formatCompletionDate,
  getPropertyAvailableUnits,
  getPropertyUnits,
  nameToColorPropertyMap,
} from '@/helpers/properties';
import { convertCurrency } from '@/helpers/currency.helper';
import { useCurrentAccountManager } from '@/api/queries/accountManagers';
import { usePropertyCommissionEstimate } from '~/composables/usePropertyCommissionEstimate';
import { useFavoritesManager } from '@/composables/useFavoritesManager';
import { useAuthStore } from '@/store/auth';

import PropertyV2Back from '@/components/property/v2/Back.vue';
import PropertyV2Crumb from '@/components/property/v2/Crumb.vue';
import PropertyV2TitleRow from '@/components/property/v2/TitleRow.vue';
import PropertyV2TabsRow, { type TabDef } from '@/components/property/v2/TabsRow.vue';
import PropertyV2HeroCard from '@/components/property/v2/HeroCard.vue';
import PropertyV2CommissionBlock from '@/components/property/v2/CommissionBlock.vue';
import PropertyV2AccountManagerSidebar from '@/components/property/v2/AccountManagerSidebar.vue';
import PropertyV2DeveloperBriefing from '@/components/property/v2/DeveloperBriefing.vue';
import PropertyV2DeveloperVaultSection from '@/components/property/v2/DeveloperVaultSection.vue';
import PropertyGallery from '@/components/property/Gallery.vue';
import OverviewTab from '@/components/property/v2/tabs/Overview.vue';
import AvailabilityTab from '@/components/property/v2/tabs/Availability.vue';
import FloorPlansTab from '@/components/property/v2/tabs/FloorPlans.vue';
import LayoutsTab from '@/components/property/v2/tabs/Layouts.vue';
import AmenitiesTab from '@/components/property/v2/tabs/Amenities.vue';
import DocumentsTab from '@/components/property/v2/tabs/Documents.vue';
import LocationTab from '@/components/property/v2/tabs/Location.vue';
import DeveloperTab from '@/components/property/v2/tabs/Developer.vue';
import NewsTab from '@/components/property/v2/tabs/News.vue';

import AccessTopNav from '@/components/property/access/AccessTopNav.vue';
import GuestSignupBanner from '@/components/property/access/GuestSignupBanner.vue';
import RegisteredVerifyChip from '@/components/property/access/RegisteredVerifyChip.vue';
import AccessTabsRow from '@/components/property/access/AccessTabsRow.vue';
import PublicPriceCard from '@/components/property/access/PublicPriceCard.vue';
import LockedCard from '@/components/property/access/LockedCard.vue';
import CommissionEstimateCard from '@/components/property/access/CommissionEstimateCard.vue';
import DevAccountManagerLocked from '@/components/property/access/DevAccountManagerLocked.vue';
import DevSalesManagerCard from '@/components/property/access/DevSalesManagerCard.vue';
import ProjectNewsFeedTeaser from '@/components/property/access/ProjectNewsFeedTeaser.vue';
import GuestExploreNearby from '@/components/property/access/GuestExploreNearby.vue';
import GuestBottomCTA from '@/components/property/access/GuestBottomCTA.vue';
import RegisteredBottomVerifyBanner from '@/components/property/access/RegisteredBottomVerifyBanner.vue';
import AccessGateModalsHost from '@/components/property/access/modals/AccessGateModalsHost.vue';
import SharePopover from '@/components/property/SharePopover.vue';
import UpcomingPresentations from '@/components/developerPartner/presentations/UpcomingPresentations.vue';
import ExclusiveCommissionBlock from '@/components/property/exclusive/ExclusiveCommissionBlock.vue';
import ExclusiveInsidersBanner from '@/components/property/exclusive/ExclusiveInsidersBanner.vue';
import ExclusiveAboutProject from '@/components/property/exclusive/ExclusiveAboutProject.vue';
import ExclusiveProjectContacts from '@/components/property/exclusive/ExclusiveProjectContacts.vue';
import ExclusiveUnitDialog from '@/components/property/exclusive/ExclusiveUnitDialog.vue';
import { useExclusiveSalesOffer } from '@/api/mutations/exclusives/useExclusiveSalesOffer';
import ExclusiveHoldActions from '@/components/property/exclusive/ExclusiveHoldActions.vue';
import ExclusiveHoldDialog from '@/components/property/exclusive/ExclusiveHoldDialog.vue';
import ExclusiveMyHolds from '@/components/property/exclusive/ExclusiveMyHolds.vue';
import ExclusiveUnitActionCell from '@/components/property/exclusive/ExclusiveUnitActionCell.vue';
import type { ExclusiveBlock, ExclusiveDetail, ExclusiveUnitRow } from '~/types/exclusives';
import { useExclusiveUnits } from '~/composables/useExclusiveUnits';
import { mergeExclusiveApartments } from '~/utils/exclusives/exclusiveApartments';

import { useAccessLevel } from '@/composables/useAccessLevel';
import { provideAccessGate, type GateAction } from '@/composables/useAccessGate';
import { useRedirectAfterAuth } from '@/composables/useRedirectAfterAuth';
import { usePropertyShareStatsQuery } from '@/composables/usePropertyShareStatsQuery';
import { fuzzyUnitBands } from '@/utils/fuzzyUnitBands';
import { slugify } from '@/utils/textSlug';
import { normalizeDeveloperName } from '@/utils/developerName';
import { pickLocalized, type LocalizedField } from '@/utils/i18n/localizedField';
import { useNewsListQuery, type NewsItem } from '@/api/queries/useNewsListQuery';
import { useAgentMe } from '@/api/queries/agent';
import { useDeveloperContactsQuery } from '@/composables/useDeveloperContactsQuery';
import { useNearbyProjectsQuery, type NearbyProject } from '@/composables/useNearbyProjectsQuery';
import SalesOfferWizard from '~/components/property/SalesOfferWizard.vue';
import { useUIStore } from '@/store/ui';

type ShellProperty = Record<string, unknown> & {
  id?: string | number;
  complex_id?: string | number;
};

const { property } = defineProps<{
  property: ShellProperty | null;
}>();

const preferences = usePreferencesStore();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { t, locale } = useI18n();

// ----- Access tier wiring -----
const accessLevel = useAccessLevel();
const { gate } = provideAccessGate();
// Builds /auth/{register|login}?redirect=<current-path> so guests come back
// to this exact project page after completing signup/signin.
const { buildRedirectUrl } = useRedirectAfterAuth();

// ----- Title row + breadcrumb -----
const projectTitle = computed(() => {
  const tEn = (property as { title?: { en?: string } } | null | undefined)?.title?.en;
  return tEn || '';
});

const developerName = computed(() => {
  const dev = (property as { developer?: { title?: { en?: string } | string } } | null | undefined)?.developer;
  if (!dev) return '';
  if (typeof dev.title === 'string') return dev.title;
  return dev.title?.en || '';
});

// Display-only names in the UI locale (v2 payload may carry title.ru/.ar and
// developer.title.ru). Slugs, lookups, deal registration and sharing keep the
// English names above.
const projectTitleDisplay = computed(() =>
  pickLocalized((property as { title?: LocalizedField } | null | undefined)?.title, locale.value, projectTitle.value),
);
const developerNameDisplay = computed(() =>
  pickLocalized(
    (property as { developer?: { title?: LocalizedField } } | null | undefined)?.developer?.title,
    locale.value,
    developerName.value,
  ),
);

// Prefer a pre-computed developerSlug on the property payload; fall back to
// deriving from the developer name so links work even when the API doesn't
// populate the slug field yet.
const developerSlug = computed(() => {
  const explicit = (property as { developerSlug?: string } | null | undefined)?.developerSlug;
  if (explicit && typeof explicit === 'string' && explicit.length) return explicit;
  return developerName.value ? normalizeDeveloperName(developerName.value) : '';
});

const refLabel = computed(() => {
  const id =
    (property as { complex_id?: string | number } | null | undefined)?.complex_id ??
    (property as { id?: string | number } | null | undefined)?.id;
  if (!id) return '';
  return t('property.shell.projectRef', { id: String(id) });
});

const formatQuarterYear = (quarter: number, year: number) => t('property.shared.quarterYear', { quarter, year });

const completionLabel = computed(() => {
  const at = (property as { planned_completion_at?: string } | null | undefined)?.planned_completion_at;
  return at ? formatCompletionDate(at, formatQuarterYear) : null;
});

const titleMetaItems = computed(() => {
  const items: string[] = [];
  const addr = (property as { address?: string } | null | undefined)?.address;
  if (addr) items.push(addr);
  if (completionLabel.value) items.push(completionLabel.value);
  return items;
});

// ----- Hero card -----
const propertyDetailPriceMin = computed(() => {
  const min = (property as { price?: { min?: number | string } } | null | undefined)?.price?.min;
  if (min == null || min === '') return 0;
  const fromCurrency = (property as { price?: { currency?: string } } | null | undefined)?.price?.currency || 'AED';
  return convertCurrency(Number(min), fromCurrency, preferences.currency);
});

const availableUnitTypes = computed(() => {
  const apartments = (property as { apartments?: unknown[] } | null | undefined)?.apartments;
  if (!apartments?.length) return [];
  const units = getPropertyUnits(apartments as never);
  const SHORT_LABEL_KEYS: Record<string, string> = {
    Studio: 'common.priceTable.studio',
    '1 bedroom': 'property.shell.unitTypeShort.oneBed',
    '2 bedroom': 'property.shell.unitTypeShort.twoBed',
    '3 bedroom': 'property.shell.unitTypeShort.threeBed',
    '4 bedroom': 'property.shell.unitTypeShort.fourBed',
    '5 bedroom': 'property.shell.unitTypeShort.fiveBed',
    '6 bedroom': 'property.shell.unitTypeShort.sixBed',
    '7 bedroom': 'property.shell.unitTypeShort.sevenBed',
  };
  return units
    .filter((u: { title?: string }) => u.title && u.title !== 'No units available')
    .map((u: { title: string }) => (SHORT_LABEL_KEYS[u.title] ? t(SHORT_LABEL_KEYS[u.title]) : u.title));
});

const progressPercentage = computed(() => {
  const raw = (property as { construction_progress?: number | string } | null | undefined)?.construction_progress;
  if (raw == null) return null;
  // Postgres stores construction_progress as a %-suffixed string
  // ("61.09%", "100%", "0%"). The legacy Geniemap source returned a plain
  // number, so the old Number(raw) worked; after the postgres migration
  // Number("61.09%") → NaN and the progress bar silently vanished. Strip
  // everything but digits/decimal before parsing.
  const cleaned = typeof raw === 'string' ? raw.replace(/[^0-9.]/g, '') : String(raw);
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : null;
});

const brochureUrl = computed(() => {
  const list = (property as { brochures?: Array<{ src: string }> } | null | undefined)?.brochures;
  return list?.length ? list[0].src : null;
});

const propertyComplexIdRaw = computed<string | null>(() => {
  const p = property as { complex_id?: string | number; id?: string | number } | null | undefined;
  const raw = p?.complex_id ?? p?.id;
  return raw != null && String(raw).length ? String(raw) : null;
});

// Raw postgres projects.id (no geniemap COALESCE, no p- prefix). This is
// the canonical key for joins on app-side data — training videos, future
// project-tagged content. Independent of geniemap presence so old URL
// id-form mismatches don't break linking.
const propertyPostgresId = computed<string | null>(() => {
  const p = property as { id?: string | number } | null | undefined;
  const raw = p?.id;
  return raw != null && String(raw).length ? String(raw) : null;
});

const offerLink = computed(() => `/offers?project=${propertyComplexIdRaw.value ?? ''}`);

// Deep-link to the marketing generator with this project preselected. The wizard
// keys off `project` (the complex_id, same id its own search returns); name/dev/
// area are passed for instant display so the agent skips straight to "Objective".
const marketingBannerTo = computed(() => ({
  path: '/marketing',
  query: {
    project: propertyComplexIdRaw.value ?? '',
    name: projectTitle.value || undefined,
    dev: developerName.value || undefined,
    area: (property as { address?: string } | null | undefined)?.address || undefined,
  },
}));

const salesStatusLabel = computed(() => {
  const raw = (property as { sales_status?: { en?: string } } | null | undefined)?.sales_status?.en;
  // The status as the source states it ("On Sale" was shown as "Presale (EOI)" until meeting 2026-11).
  return raw || '';
});

const salesStatusColor = computed(() => {
  return nameToColorPropertyMap[salesStatusLabel.value] || '#3957D3';
});

// Server-set per viewer (ID-117); guests and UAE agents without an approved Emirates ID always get false.
type DeveloperUpdate = {
  updatedAt: string | null;
  availability: string | null;
  propertyTypes: string[];
  commissionPromotion?: string | null;
};
const developerFields = computed(
  () =>
    property as
      | { developerUpdate?: DeveloperUpdate; isDeveloperSubmitted?: boolean; developerId?: number | null; id?: string }
      | null
      | undefined,
);
const developerUpdate = computed(() => developerFields.value?.developerUpdate ?? null);
const isDeveloperSubmitted = computed(() => !!developerFields.value?.isDeveloperSubmitted);
const presentationsDeveloperId = computed(() => developerFields.value?.developerId ?? null);
// Presentations link to bot project ids only; rep-created projects (d-<id>) show the developer's sessions.
const presentationsProjectId = computed(() => {
  const id = developerFields.value?.id;
  return id && /^\d+$/.test(id) ? Number(id) : undefined;
});
const { formatDate } = useFormat();
const formatDeveloperUpdateDate = (iso: string) => formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });

const exclusiveBlock = computed(() => (property as { exclusive?: ExclusiveBlock } | null)?.exclusive ?? null);
const exclusiveCatalogueProjectId = computed(() =>
  exclusiveBlock.value?.isExclusive ? exclusiveBlock.value.catalogueProjectId : null,
);
// Verified viewers only (ID-117 detail payload).
const exclusiveDetail = computed(
  () => (property as { exclusiveDetail?: ExclusiveDetail } | null)?.exclusiveDetail ?? null,
);
// Set once the live units endpoint reports the project stopped being exclusive while the page is open.
const exclusiveEnded = ref(false);
const showExclusive = computed(() => !!exclusiveCatalogueProjectId.value && !exclusiveEnded.value);
const showExclusiveCommission = computed(
  () => showExclusive.value && accessLevel.value === 'verified' && !isClientMode.value,
);
// Live exclusive units (polled), shown in the normal Availability tab with the hold action (ID-118 / ID-119).
const exclusiveUnitsLive = useExclusiveUnits(() => (showExclusive.value ? exclusiveCatalogueProjectId.value : null));
const exclusiveUnitsPayload = computed(() => (showExclusive.value ? (exclusiveUnitsLive.data.value ?? null) : null));
const exclusiveHolds = computed(() => exclusiveUnitsPayload.value?.myHolds ?? []);
watch(exclusiveUnitsLive.noLongerExclusive, (ended) => {
  if (ended) exclusiveEnded.value = true;
});
watch(exclusiveUnitsLive.sessionExpired, (expired) => {
  if (expired) onExclusiveSessionExpired();
});

// The property as the tabs see it: exclusive units merged into `apartments` once the live list has loaded.
const tabItem = computed(() => {
  const payload = exclusiveUnitsPayload.value;
  if (!property || !payload || !exclusiveCatalogueProjectId.value) return property;
  const existing = (property as { apartments?: unknown[] }).apartments ?? [];
  return {
    ...property,
    apartments: mergeExclusiveApartments(exclusiveCatalogueProjectId.value, payload.units, existing),
  };
});

type AvailabilityActionRow = { catalogueUnitId: number | null; statusKey: string };

const exclusiveUnitById = (id: number | null): ExclusiveUnitRow | null =>
  id === null ? null : (exclusiveUnitsPayload.value?.units.find((u) => u.catalogueUnitId === id) ?? null);

const isHoldableExclusiveRow = (row: AvailabilityActionRow) =>
  exclusiveUnitById(row.catalogueUnitId)?.status === 'available';

// ID-119 holds: the dialog opens for the unit whose "Hold unit" was pressed; the list refreshes after a hold or release.
const holdingExclusiveUnit = ref<ExclusiveUnitRow | null>(null);
const onHoldExclusiveRow = (row: AvailabilityActionRow) => {
  holdingExclusiveUnit.value = exclusiveUnitById(row.catalogueUnitId);
};
// Unit popup (meeting 2026-11): opened from the unit number on an exclusive Availability row.
const selectedExclusiveUnit = ref<ExclusiveUnitRow | null>(null);
const onSelectAvailabilityRow = (row: AvailabilityActionRow) => {
  selectedExclusiveUnit.value = exclusiveUnitById(row.catalogueUnitId);
};
const onHoldFromUnitDialog = (unit: ExclusiveUnitRow) => {
  selectedExclusiveUnit.value = null;
  holdingExclusiveUnit.value = unit;
};
const refreshExclusiveUnits = () => {
  void exclusiveUnitsLive.refresh();
};
const onViewExclusiveDeal = (dealId: string) => navigateTo({ path: '/deals', query: { deal: dealId } });

// ----- Tabs -----
const tabDefinitions = computed<TabDef[]>(() => {
  const out: TabDef[] = [{ id: 'overview', label: t('property.shell.tabs.overview') }];

  const apartments = (property as { apartments?: unknown[] } | null | undefined)?.apartments;
  if (apartments?.length) {
    out.push({ id: 'availability', label: t('property.shell.tabs.availability') });
  }

  // Layouts tab: unit-type metadata (unit_layouts), real floor/site-plan images
  // (image_meta floorplan/site_plan tags), and/or floor-plan PDFs (documents).
  const layoutBearing = property as
    | { layouts?: unknown[]; availableLayouts?: unknown[]; layoutImages?: unknown[]; floor_plans?: unknown[] }
    | null
    | undefined;
  if (
    layoutBearing?.layouts?.length ||
    layoutBearing?.availableLayouts?.length ||
    layoutBearing?.layoutImages?.length ||
    layoutBearing?.floor_plans?.length
  ) {
    out.push({ id: 'layouts', label: t('property.shell.tabs.layouts') });
  }

  // NOTE: the internal 'floorPlans' tab renders the PAYMENT-PLAN milestones
  // (FloorPlansTab) — labelled accordingly. Unit/floor layouts are the separate
  // 'layouts' tab above.
  // Show the tab when ANY plan has real content (a name or items). Don't gate
  // on `id` — manually-added plans (e.g. sales-manager-supplied) have no id,
  // only name/items, so the old `paymentPlans[0]?.id` check hid the tab for
  // them despite valid plans. Mirrors usePropertyPaymentPlans' name filter.
  const paymentPlans = (property as { payment_plans?: Array<{ name?: string; items?: unknown[] }> } | null | undefined)
    ?.payment_plans;
  if (
    Array.isArray(paymentPlans) &&
    paymentPlans.some((p) => p && (!!p.name || (Array.isArray(p.items) && p.items.length > 0)))
  ) {
    out.push({ id: 'floorPlans', label: t('property.shell.tabs.paymentPlan') });
  }

  const amenities = (property as { amenities?: unknown[] } | null | undefined)?.amenities;
  if (amenities?.length) {
    out.push({ id: 'amenities', label: t('property.shell.tabs.amenities') });
  }

  // Always include the Documents tab — the empty state inside DocumentsTab handles
  // the "no brochures / floor plans / videos" case. Gating the tab on docsAvailable
  // caused the AccessTabsRow click to silently no-op (the tab is always rendered
  // there) so users perceived the tab as broken.
  out.push({ id: 'documents', label: t('property.shell.tabs.documents') });

  out.push({ id: 'location', label: t('property.shell.tabs.location') });
  out.push({ id: 'developer', label: t('property.shell.tabs.developer') });
  out.push({ id: 'news', label: t('property.shell.tabs.news') });

  return out;
});

const activeTab = ref<string>('overview');

watch(tabDefinitions, (defs) => {
  if (!defs.some((d) => d.id === activeTab.value)) {
    activeTab.value = 'overview';
  }
});

const setActiveTab = (id: string) => {
  activeTab.value = id;
};

const tabComponentMap: Record<string, ReturnType<typeof markRaw>> = {
  overview: markRaw(OverviewTab),
  availability: markRaw(AvailabilityTab),
  layouts: markRaw(LayoutsTab),
  floorPlans: markRaw(FloorPlansTab),
  amenities: markRaw(AmenitiesTab),
  documents: markRaw(DocumentsTab),
  location: markRaw(LocationTab),
  developer: markRaw(DeveloperTab),
  news: markRaw(NewsTab),
};

const getTabComponent = (id: string) => tabComponentMap[id] || tabComponentMap.overview;

const onBookUnit = () => {
  registerSelectedUnit();
};

// ----- Commission block -----
// Once apartments come from offplan_listings (one row per real unit),
// `getPropertyAvailableUnits` would produce one dropdown option per unit —
// untenable on big projects (Vision Iconic has 864 units). For ≤ 12 units
// we still show the per-unit picker (richer UX); above that we group by
// bedroom bucket so the dropdown stays scannable.
const PER_UNIT_PICKER_CAP = 12;

const propertyUnits = computed(() => {
  const apartments = (property as { apartments?: unknown[] } | null | undefined)?.apartments;
  if (!apartments?.length) return [];
  const available = getPropertyAvailableUnits(apartments as never);
  if (available.length && available.length <= PER_UNIT_PICKER_CAP) return available;
  return getPropertyUnits(apartments as never) as Array<{
    name: string;
    title: string;
    price_min: number;
    currency?: string;
  }>;
});

const selectedUnitKey = ref<string | null>(null);

watch(
  propertyUnits,
  (units) => {
    if (!units.length) {
      selectedUnitKey.value = null;
      return;
    }
    if (!selectedUnitKey.value || !units.find((u) => u.name === selectedUnitKey.value)) {
      selectedUnitKey.value = units[0].name;
    }
  },
  { immediate: true },
);

const selectedUnit = computed(() => {
  const units = propertyUnits.value;
  if (!units.length || !selectedUnitKey.value) return null;
  return units.find((u) => u.name === selectedUnitKey.value) ?? null;
});

const selectedUnitPriceInBaseCurrency = computed<number | null>(() => {
  const u = selectedUnit.value;
  if (!u || typeof u.price_min !== 'number' || !Number.isFinite(u.price_min)) return null;
  return u.price_min;
});

const propertyRef = computed(() => property);

const {
  isAgent: isAgentForCommission,
  isUaeShare: isAgentUaeShare,
  isSplitPending: isAgentSplitPending,
  poolPercent: projectCommissionPoolPercent,
  tierSplitPercent: agentTierSplitPercent,
  effectivePercentOfPrice: agentEffectiveCommissionPercent,
  estimatedAmountFromMinPrice: estimatedCommissionFromPoolAndTier,
  commissionBadgeTitle: agentCommissionTooltip,
} = usePropertyCommissionEstimate(propertyRef, selectedUnitPriceInBaseCurrency);

const { data: agentStats } = useAgentStats();
const agentTierLabel = computed(() => {
  const tier = agentStats.value?.tier as string | undefined;
  if (!tier) return null;
  const split = agentTierSplitPercent.value;
  const name = `${tier.charAt(0).toUpperCase()}${tier.slice(1).toLowerCase()}`;
  if (isAgentSplitPending.value) return name;
  return split != null ? `${name} · ${split}% split` : tier;
});

const commissionDisplay = computed(() => agentEffectiveCommissionPercent.value);
const estimatedCommissionValue = computed(() => estimatedCommissionFromPoolAndTier.value);
const commissionEstimateCurrency = computed(
  () => (property as { price?: { currency?: string } } | null | undefined)?.price?.currency || 'AED',
);

const dealRegistrationStore = useDealRegistrationStore();

// Co-broke brokers (ID-133) hold units and register deals on exclusive projects only, while their agency is active.
const { data: coBrokeAccess } = useCoBrokeAccessQuery();
const coBrokeNotice = computed(() => {
  if (!coBrokeAccess.value?.isCoBroke) return null;
  if (!showExclusive.value) return t('deals.coBroke.exclusivesOnly');
  return coBrokeAccess.value.canSell ? null : t('deals.coBroke.paused');
});

const registerSelectedUnit = async () => {
  if (coBrokeNotice.value) {
    uiStore.showSnackBanner('error', coBrokeNotice.value);
    return;
  }
  const p = property as
    | {
        id?: string | number;
        complex_id?: string | number;
        title?: { en?: string };
        price?: { currency?: string };
      }
    | null
    | undefined;
  const projectId = p?.id || p?.complex_id || propertyComplexIdRaw.value;
  if (!projectId) return;
  dealRegistrationStore.setPrePopulation({
    projectId: String(projectId),
    projectName: p?.title?.en ?? '',
    unitTypeKey: selectedUnit.value?.name ?? null,
    unitTypeLabel: (selectedUnit.value as { title?: string } | null)?.title ?? null,
    unitPrice: selectedUnitPriceInBaseCurrency.value,
    currency: p?.price?.currency ?? 'AED',
  });
  await navigateTo('/deals/register');
};

// ----- Account manager sidebar -----
const { data: accountManager } = useCurrentAccountManager();

// ----- Favorites -----
const favoritesManager = useFavoritesManager();
const isFavorite = computed(() => {
  const id =
    (property as { id?: string; complex_id?: string } | null | undefined)?.id ??
    (property as { complex_id?: string } | null | undefined)?.complex_id;
  if (!id) return false;
  return typeof (favoritesManager as { isFavorite?: (id: string) => boolean }).isFavorite === 'function'
    ? (favoritesManager as { isFavorite: (id: string) => boolean }).isFavorite(String(id))
    : false;
});

const onToggleFavorite = async () => {
  if (!authStore.token) {
    await navigateTo(buildRedirectUrl('/auth/register'));
    return;
  }
  const id =
    (property as { id?: string; complex_id?: string } | null | undefined)?.id ??
    (property as { complex_id?: string } | null | undefined)?.complex_id;
  if (!id) return;
  const fm = favoritesManager as {
    toggleFavorite?: (id: string) => Promise<void> | void;
    addFavorite?: (id: string) => Promise<void> | void;
    removeFavorite?: (id: string) => Promise<void> | void;
  };
  if (typeof fm.toggleFavorite === 'function') {
    await fm.toggleFavorite(String(id));
  } else if (isFavorite.value && typeof fm.removeFavorite === 'function') {
    await fm.removeFavorite(String(id));
  } else if (typeof fm.addFavorite === 'function') {
    await fm.addFavorite(String(id));
  }
};

// ----- Briefing banner -----
type BriefingVideo = {
  title: string;
  description: string;
  thumbnail: string | null;
  durationLabel: string | null;
  metaItems: string[];
  link: string;
};

const briefingVideo = computed<BriefingVideo | null>(() => {
  const videos = (
    property as
      | { videos?: Array<{ src: string; title?: string; description?: string; thumbnail?: string }> }
      | null
      | undefined
  )?.videos;
  if (!videos?.length) return null;
  const first = videos[0];
  return {
    title: first.title || projectTitle.value,
    description: first.description || '',
    thumbnail: first.thumbnail || null,
    durationLabel: null,
    metaItems: developerName.value ? [developerName.value] : [],
    link: '/developer-vault',
  };
});

// ----- Misc -----
const { isOn: isClientMode } = useClientMode();
const isAuth = computed(() => !!authStore.user);

const goBack = () => {
  const fromQuery = route.query.from;
  if (fromQuery === 'portfolio') {
    navigateTo('/portfolio');
    return;
  }
  const hasInAppHistory =
    typeof window !== 'undefined' && !!(window.history.state && (window.history.state as { back?: unknown }).back);
  if (hasInAppHistory) {
    router.back();
    return;
  }
  navigateTo('/explore');
};

// ----- Access page wiring -----
const ACCESS_TO_INTERNAL: Record<string, string> = {
  general: 'overview',
  location: 'location',
  layouts: 'layouts',
  availability: 'availability',
  price: 'floorPlans',
  amenities: 'amenities',
  developer: 'developer',
  documents: 'documents',
};

const INTERNAL_TO_ACCESS: Record<string, string> = {
  overview: 'general',
  location: 'location',
  layouts: 'layouts',
  floorPlans: 'price',
  availability: 'availability',
  amenities: 'amenities',
  developer: 'developer',
  documents: 'documents',
  news: 'general',
};

const accessActiveTab = computed(() => INTERNAL_TO_ACCESS[activeTab.value] || 'general');

const hiddenTabsForLevel = computed(() => {
  if (accessLevel.value === 'guest') return ['availability', 'documents'];
  return [];
});

const lockedTabsForLevel = computed(() => {
  if (accessLevel.value === 'registered') return ['availability'];
  return [];
});

const onAccessTabClick = (key: string) => {
  const internal = ACCESS_TO_INTERNAL[key];
  if (!internal) return;
  if (tabDefinitions.value.some((d) => d.id === internal)) {
    setActiveTab(internal);
  }
};

const onLockedTab = (key: string) => {
  const action: GateAction = key === 'availability' ? 'availability' : 'generic';
  gate(action, () => {
    const internal = ACCESS_TO_INTERNAL[key];
    if (internal && tabDefinitions.value.some((d) => d.id === internal)) {
      setActiveTab(internal);
    }
  });
};

// Public price card needs the "developer" name in `{ en: string }` shape.
const publicPriceProperty = computed(() => {
  if (!property) return null;
  const p = property as {
    title?: { en?: string };
    price?: { min?: number | string; currency?: string };
    payment_plan?: string;
  };
  return {
    title: { en: p.title?.en || '' },
    developer: developerName.value ? { en: developerName.value } : null,
    price: { min: p.price?.min ?? 0, currency: p.price?.currency || 'AED' },
    paymentPlan: p.payment_plan ?? null,
    handoverYear: completionLabel.value,
  };
});

const publicUnitBands = computed(() => {
  const apartments = (property as { apartments?: unknown[] } | null | undefined)?.apartments;
  return fuzzyUnitBands(apartments);
});

// ----- Commission estimate (registered) -----
const sampleAptPrice = computed<number>(() => {
  const min = (property as { price?: { min?: number | string } } | null | undefined)?.price?.min;
  const n = typeof min === 'number' ? min : Number(String(min ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
});

const SAMPLE_BROKERAGE_RATE = 0.04;
const SAMPLE_TIER_PERCENT = 60;

const sampleBrokerageAed = computed(() => Math.round(sampleAptPrice.value * SAMPLE_BROKERAGE_RATE));
const sampleShareAed = computed(() => Math.round(sampleBrokerageAed.value * (SAMPLE_TIER_PERCENT / 100)));
const sampleUnitLabel = computed(() => {
  const bands = publicUnitBands.value;
  return bands.length ? bands[0].label.split(' ·')[0] : '2BR';
});

// ----- Developer account managers (registered locked card) -----
type DevManagerLockedRow = { displayName: string; role: string; maskedPhone?: string; maskedEmail?: string };
const { data: developerContactsData } = useDeveloperContactsQuery(developerName);
const developerManagers = computed<DevManagerLockedRow[]>(() => {
  const rows = developerContactsData.value?.managers ?? [];
  return rows.map((m) => ({
    displayName: m.displayName,
    role: m.role,
    maskedPhone: m.phone ?? undefined,
    maskedEmail: m.email ?? undefined,
  }));
});

// Verified view: the endpoint returns the real (unmasked) phone + WhatsApp,
// which we render as a tappable sales-manager card.
const developerManagersVerified = computed(() => {
  const rows = developerContactsData.value?.managers ?? [];
  return rows.map((m) => ({
    name: m.displayName,
    role: m.role,
    phone: m.phone,
    whatsapp: m.whatsapp,
    avatarUrl: m.avatarUrl,
  }));
});

// ----- News teaser (guest) -----
const projectIdNumber = computed<number | undefined>(() => {
  const raw = Number((property as { complex_id?: number | string } | null | undefined)?.complex_id);
  return Number.isFinite(raw) && raw > 0 ? raw : undefined;
});

const newsTeaserParams = computed(() => ({
  pageSize: 6,
  ...(projectIdNumber.value !== undefined ? { projectId: projectIdNumber.value } : {}),
}));

const { data: newsTeaserData } = useNewsListQuery(newsTeaserParams);

const formatTeaserTime = (iso: string): string => {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days <= 0) return t('property.shell.teaserTime.today');
    if (days === 1) return t('property.shell.teaserTime.daysAgo', { count: 1 }, 1);
    if (days < 30) return t('property.shell.teaserTime.daysAgo', { count: days }, days);
    const months = Math.floor(days / 30);
    return t('property.shell.teaserTime.monthsAgo', { count: months }, months);
  } catch {
    return '';
  }
};

const newsTeaserItems = computed(() => {
  const list: NewsItem[] = newsTeaserData.value?.items ?? [];
  const tagFor = (n: NewsItem): { tag: string; cls: 'launch' | 'milestone' } => {
    if (n.type === 'HANDOVER' || n.type === 'INVENTORY')
      return { tag: t('property.shell.newsTag.launch'), cls: 'launch' };
    return { tag: t('property.shell.newsTag.update'), cls: 'milestone' };
  };
  return list.slice(0, 2).map((n) => {
    const t = tagFor(n);
    return {
      tag: t.tag,
      tagCls: t.cls,
      time: formatTeaserTime(n.publishedAt),
      unread: !n.isRead,
      title: n.title,
      message: n.body,
    };
  });
});

const newsHiddenCount = computed(() => {
  const total = newsTeaserData.value?.items?.length ?? 0;
  return Math.max(0, total - 2);
});

// ----- Guest "explore nearby" sidebar -----
const exploreArea = computed(() => {
  const addr = property as { address?: string; emirate?: string } | null | undefined;
  return addr?.address || addr?.emirate || 'Dubai';
});

const nearbyArea = computed<string | null>(() => {
  const p = property as
    | {
        area_slug?: string;
        district_slug?: string;
        area?: string;
        district?: string;
        districts?: Array<string | { name?: string; en?: string; slug?: string }>;
        address?: string;
      }
    | null
    | undefined;
  if (!p) return null;

  // Prefer pre-computed slug fields when present.
  const directSlug = p.area_slug || p.district_slug;
  if (directSlug) return directSlug;

  // Pick the first district (string or {name|en|slug}).
  const firstDistrict = Array.isArray(p.districts) ? p.districts[0] : null;
  const districtName =
    typeof firstDistrict === 'string'
      ? firstDistrict
      : firstDistrict?.slug || firstDistrict?.name || firstDistrict?.en || '';
  const candidate = districtName || p.area || p.district || '';

  if (candidate) return slugify(candidate);

  // Fallback: derive area from address ("One River Point, Business Bay, Dubai" → "Business Bay").
  const parts = (p.address || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length >= 2) return slugify(parts[parts.length - 2]);
  if (parts.length === 1) return slugify(parts[0]);

  return null;
});
const nearbyExcludeId = computed<string | null>(() => propertyComplexIdRaw.value);
const { data: nearbyProjectsData } = useNearbyProjectsQuery(nearbyArea, nearbyExcludeId);
const exploreNearby = computed<NearbyProject[]>(() => nearbyProjectsData.value ?? []);

// ----- Action handlers -----
const onSignup = () => {
  navigateTo(buildRedirectUrl('/auth/register'));
};

const onSignin = () => {
  navigateTo(buildRedirectUrl('/auth/login'));
};

const onVerifyClick = () => {
  gate('availability');
};

// Units polling hit 401: clear the dead session and send the agent to sign in, then back here.
const { logout } = useSuccessfulLogout();
const onExclusiveSessionExpired = () => {
  void logout(buildRedirectUrl('/auth/login'));
};

const onEnquireClick = () => {
  if (accessLevel.value === 'verified') {
    registerSelectedUnit();
    return;
  }
  gate('generic', () => navigateTo(buildRedirectUrl('/auth/register')));
};

const onSaveClick = () => {
  if (accessLevel.value === 'verified') {
    onToggleFavorite();
    return;
  }
  gate('generic', () => navigateTo(buildRedirectUrl('/auth/register')));
};

const onExploreItemClick = (title: string) => {
  const match = (nearbyProjectsData.value ?? []).find((n) => n.title === title);
  if (match?.slug) {
    navigateTo(`/p/${match.slug}`);
  }
};

// ----- Sales offer wizard (project overview, or a unit with a 24h price hold) -----
const uiStore = useUIStore();
// The wizard shows its own progress; the hero button only opens it.
const isGeneratingOffer = false;
const exclusiveSalesOffer = useExclusiveSalesOffer();

const offerDialogOpen = ref(false);
// Set while the offer dialog prepares an offer for an exclusive (catalogue) unit from the unit popup.
const exclusiveOfferUnit = ref<ExclusiveUnitRow | null>(null);
const closeOfferDialog = () => {
  offerDialogOpen.value = false;
  exclusiveOfferUnit.value = null;
};

const onPrepareExclusiveOffer = (unit: ExclusiveUnitRow) => {
  if (coBrokeNotice.value) {
    uiStore.showSnackBanner('error', coBrokeNotice.value);
    return;
  }
  if (accessLevel.value !== 'verified') {
    selectedExclusiveUnit.value = null;
    onVerifyClick();
    return;
  }
  selectedExclusiveUnit.value = null;
  exclusiveOfferUnit.value = unit;
};

const submitExclusiveOffer = async (unit: ExclusiveUnitRow, clientName: string | undefined, hideDeveloper: boolean) => {
  const catalogueProjectId = exclusiveCatalogueProjectId.value;
  if (!catalogueProjectId) return;
  const res = await exclusiveSalesOffer.mutateAsync({
    catalogueProjectId,
    catalogueUnitId: unit.catalogueUnitId,
    clientName,
    hideDeveloper,
  });
  closeOfferDialog();
  if (res.offerUrl && typeof window !== 'undefined') window.open(res.offerUrl, '_blank', 'noopener');
  uiStore.showSnackBanner('success', t('property.shell.offerReady'));
};

const generateOfferAndOpen = () => {
  if (coBrokeNotice.value) {
    uiStore.showSnackBanner('error', coBrokeNotice.value);
    return;
  }
  if (!propertyComplexIdRaw.value) return;
  offerDialogOpen.value = true;
};

// "Prepared for" client name (optional) and the white-label option, from the exclusive-unit dialog.
const submitExclusiveFromDialog = async ({
  clientName,
  hideDeveloper,
}: {
  clientName?: string;
  hideDeveloper: boolean;
}) => {
  if (exclusiveOfferUnit.value) await submitExclusiveOffer(exclusiveOfferUnit.value, clientName, hideDeveloper);
};

const onGenerateOfferClick = () => {
  if (accessLevel.value === 'verified') {
    generateOfferAndOpen();
    return;
  }
  if (accessLevel.value === 'registered') {
    gate(
      'sales-offer',
      () => {
        generateOfferAndOpen();
      },
      {
        unitLabel: (selectedUnit.value as { title?: string } | null)?.title ?? t('property.shell.selectedUnitFallback'),
        projectTitle: projectTitle.value,
      },
    );
    return;
  }
  gate('generic', () => navigateTo(buildRedirectUrl('/auth/register')));
};

// ----- Share popover -----
const shareOpen = ref(false);
const { data: agentMeData } = useAgentMe();

const agentReference = computed<string | null>(() => {
  if (accessLevel.value !== 'verified') return null;
  const me = agentMeData.value as
    | { agentReference?: string | null; agentProfile?: { agentReference?: string | null } | null }
    | null
    | undefined;
  return me?.agentProfile?.agentReference || me?.agentReference || null;
});

const shareSlug = computed(() => slugify(projectTitle.value) || (propertyComplexIdRaw.value ?? ''));
const shareDeveloperSlug = computed(() => normalizeDeveloperName(developerName.value));

const propertyGalleryFirst = computed<string | null>(() => {
  const p = property as { album?: string[]; albums?: { images?: string[] }[] } | null | undefined;
  if (p?.album?.length) return p.album[0];
  if (Array.isArray(p?.albums)) {
    for (const a of p.albums) {
      if (a?.images?.length) return a.images[0];
    }
  }
  return null;
});

const sharePropertyData = computed(() => ({
  title: { en: projectTitle.value },
  slug: shareSlug.value,
  developerSlug: shareDeveloperSlug.value,
  galleryFirst: propertyGalleryFirst.value,
  fromAed: sampleAptPrice.value,
  paymentPlan: (property as { payment_plan?: string } | null | undefined)?.payment_plan ?? null,
  quarter: completionLabel.value ?? null,
}));

const verifiedBrochureUrl = computed<string | null>(() => {
  if (accessLevel.value !== 'verified') return null;
  return brochureUrl.value;
});

const { data: shareStatsData } = usePropertyShareStatsQuery(
  computed(() => propertyComplexIdRaw.value),
  computed(() => accessLevel.value === 'verified'),
);

const onShareClick = () => {
  shareOpen.value = true;
};

const onShareTrack = async ({ channel }: { channel: string }) => {
  const id = propertyComplexIdRaw.value;
  if (!id) return;
  try {
    await $fetch(`/api/properties/${id}/share/track`, {
      method: 'POST',
      body: { channel },
      headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : undefined,
    });
  } catch {
    // Silent: tracking failure shouldn't block the share UX.
  }
};

// ----- Activation: viewed real inventory -----
// Fire-and-forget on mount for logged-in users only (guard on the token to
// avoid noise from guests). Covers both the project and property detail pages
// since both render through this shell. Never blocks or breaks the page.
onMounted(() => {
  if (!authStore.token) return;
  $fetch('/api/activation/track', {
    method: 'POST',
    body: { event: 'inventory' },
    headers: { Authorization: `Bearer ${authStore.token}` },
  }).catch(() => {});
});
</script>

<style scoped lang="scss">
.pp-page {
  background: white;
  min-height: 100%;
}

.pp-wrap {
  max-width: 1320px;
  margin: 0 auto;
  padding: 20px 32px 80px;

  @media (max-width: 744px) {
    padding: 16px 16px 60px;
  }
}

.pp-back-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}

.pa-crumbs-public {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  color: rgba(13, 15, 21, 0.6);
  letter-spacing: 0.04em;

  a {
    color: rgba(13, 15, 21, 0.7);
    text-decoration: none;

    &:hover {
      color: #0d0f15;
      text-decoration: underline;
    }
  }

  .cur {
    color: #0d0f15;
    font-weight: 600;
  }
}

.pp-title-share-wrap {
  position: relative;
}

.pp-dev-update {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  background: #fff;
  font-size: 13px;
  color: rgba(13, 15, 21, 0.75);
}

.pp-dev-update__pill {
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(176, 141, 62, 0.14);
  color: #7a5f22;
  font-weight: 600;
  font-size: 12px;
}

.pp-gallery-frame {
  margin-bottom: 24px;
}

.pp-main {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.pp-content {
  min-width: 0;
}

.pp-exclusive-ended {
  margin-top: 24px;
  font-size: 14px;
  color: rgba(13, 15, 21, 0.75);
}

.pp-exclusive-holds {
  margin-bottom: 16px;
}

// Same look as the Availability tab's own "Book" button (regular units on a 100% Commission project).
.pp-book-fallback {
  padding: 7px 12px;
  border-radius: 8px;
  background: #0d0f15;
  color: white;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  border: none;
  cursor: pointer;
}

// ── Marketing generator banner (verified agents) ──────────────────────────────
.pp-mkt-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  padding: 14px 18px;
  border-radius: 14px;
  text-decoration: none;
  color: #0d0f15;
  background: linear-gradient(100deg, #fff6e7 0%, #ffe9c4 100%);
  border: 1px solid #f3d9a4;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px -14px rgba(176, 110, 13, 0.55);
  }

  .pp-mkt-spark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 10px;
    background: #ffb648;
    color: #0d0f15;
    font-size: 18px;
  }

  .pp-mkt-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    line-height: 1.35;

    strong {
      font-weight: 700;
      font-size: 14.5px;
    }

    span {
      font-size: 13px;
      color: rgba(13, 15, 21, 0.66);
    }
  }

  .pp-mkt-cta {
    margin-inline-start: auto;
    flex-shrink: 0;
    font-weight: 700;
    font-size: 13.5px;
    color: #b06e0d;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    .pp-mkt-cta {
      display: none;
    }
  }
}

.pp-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  @media (min-width: 1024px) {
    align-self: start;
    position: sticky;
    top: 24px;
  }
}

.pp-state {
  text-align: center;
  padding: 80px 16px;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;

  &.empty {
    h2 {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 28px;
      letter-spacing: -0.02em;
      color: #0d0f15;
      margin: 0 0 8px;
    }

    p {
      max-width: 520px;
      margin: 0 auto 24px;
    }

    .cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 44px;
      padding: 0 18px;
      border-radius: 12px;
      background: #ffb648;
      color: #0d0f15;
      font-size: 13px;
      font-weight: 700;
    }
  }
}
</style>
